import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useContext
} from 'react';
import useGlobalVariables from '../../hooks/useGlobalVariables';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import SelectView from '../view/SelectView';

const tanslateValue = value => {
  let translatedValue = value;
  if (typeof value === 'object') {
    translatedValue = JSON.stringify(value);
  }
  return translatedValue;
};

const defaultIsEqual = (a, b) => {
  if (typeof a === 'object' && typeof b === 'object') {
    if (a?.value && b?.value) {
      return a.value === b.value;
    }
  }
  return tanslateValue(a) === tanslateValue(b);
};

const searchFilter = (option, userInput, translate) => {
  if (!option || !option.text || typeof option.text !== 'string') {
    return false;
  }
  let lcOption = option.text.toLowerCase();
  if (translate) {
    lcOption = translate(lcOption);
  }
  const lcUserInput = userInput.toLowerCase();
  return lcOption.includes(lcUserInput);
};

/**
 *
 * @param {Boolean} dontAllowEmptyMuliSelect - allways have at least 1 option selected
 */
export default function SelectEngine({
  variant = 'outlined',
  label,
  options: syncOptions = [],
  asyncOptions,
  // asyncDefaultOptionDisplayText,
  searchable = false,
  allowMultiSelect = false,
  aggregateMultiSelectDisplay,
  dontAllowEmptyMuliSelect,
  noArrow = false,
  onChange,
  value = '',
  placeholder = '',
  valueParser = v => v,
  isEqual = defaultIsEqual,
  openMenuOptionParser = opt => opt.text,
  searchFunction = searchFilter,
  menuProps = {},
  virtualize,
  virtualiziztionProps = {},
  disabled,
  selectRef,
  forceDisplayValue = '',
  tooltip,
  tooltipArrow,
  isUserLookup = false,
  ...inputProps
}) {
  const { isMobileView } = useGlobalVariables();
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchInputEl, setSearchInputEl] = useState(null);
  const [filterText, setFilterText] = useState();
  const [selected, setSelected] = useState(
    value && Object.keys(value) ? value : ''
  );
  const [options, setOptions] = useState(syncOptions);
  const [options2Display, setOptions2Display] = useState(options);
  const [menuWidth, setMenuWidth] = useState('auto');
  const [isLoading, setIsLoading] = useState(false);

  const { openSnackbar } = useContext(FetchCallbacksContext);

  const valueInUse = useMemo(() => {
    if (!onChange) {
      return selected;
    }
    if (allowMultiSelect && !Array.isArray(value)) {
      return [value];
    }
    return value;
  }, [value, selected, onChange]);

  useEffect(() => {
    if (!asyncOptions && !isEqual(options, syncOptions)) {
      setOptions(syncOptions);
      setOptions2Display(syncOptions);
      // if (!syncOptions.find(opt => isSelected(opt.value))) {
      //   // if options changed and the selected value was removed nullify selected

      //   // TODO: handle multi select
      //   setSelected('');
      //   onChange && onChange(''); // creates an issue with reminders
      // }
    }
  }, [JSON.stringify(syncOptions)]);

  useEffect(() => {
    const isMenuOpen = Boolean(anchorEl);
    if (!isMenuOpen || typeof filterText === 'undefined') {
      setOptions2Display(options);
      return;
    }
    const trimmedText = filterText.trim();
    if (!asyncOptions && trimmedText === '') {
      setOptions2Display(options);
      return;
    }

    let filteredOptions = [];
    let currentOptions = options;

    if (asyncOptions) {
      // TODO: rewrite this section
      setIsLoading(true);
      asyncOptions(trimmedText).then(r => {
        currentOptions = r;

        filteredOptions = currentOptions;

        if (!filteredOptions.length) {
          filteredOptions.push({
            disabled: true,
            text: 'Nothing found.',
            value: -1
          });
        }
        setIsLoading(false);
        setOptions(filteredOptions);
        setOptions2Display(filteredOptions);
      });
      return;
    }

    filteredOptions = currentOptions.filter(option =>
      searchFunction(option, trimmedText, t)
    );

    if (!filteredOptions.length) {
      filteredOptions.push({
        disabled: true,
        text: 'Nothing found.',
        value: -1
      });
    }
    setOptions2Display(filteredOptions);
  }, [anchorEl, filterText]);

  useEffect(() => {
    const isMenuOpen = Boolean(anchorEl);
    if (isMenuOpen) {
      setFilterText('');
      setMenuWidth(anchorEl.offsetWidth);
    }
  }, [anchorEl]);

  const onSelected = useCallback(
    (v, e) => {
      if (allowMultiSelect) {
        const indexOfExisting = valueInUse.findIndex(element =>
          isEqual(element, v)
        );
        const newSelected =
          indexOfExisting < 0
            ? [...valueInUse, v]
            : valueInUse.filter(element => !isEqual(element, v));

        if (dontAllowEmptyMuliSelect && newSelected.length < 1) {
          openSnackbar('error', t('snackbars.select.multi.empty.error'));
          return;
        }

        onChange ? onChange(newSelected) : setSelected(newSelected);
      } else {
        let anchorElWas = anchorEl;
        setTimeout(() => {
          if (anchorElWas) {
            anchorElWas.children[0].scrollLeft = 0;
          }
        }, 1);
        if (options && options.length) {
          const matchingOption = options.find(one => one.value === v);
          if (matchingOption?.hasSubMenu) {
            onChange ? onChange(v, e) : setSelected(v);
            return;
          } else {
            setAnchorEl(null);
          }
        } else {
          setAnchorEl(null);
        }
        if (isEqual(v, valueInUse)) {
          return;
        }
        onChange ? onChange(v, e) : setSelected(v);
      }
    },
    [
      anchorEl,
      valueInUse,
      onChange,
      openSnackbar,
      t,
      allowMultiSelect,
      dontAllowEmptyMuliSelect
    ]
  );

  /**
   * forTooltip - get full text to show regaedless of aggregateMultiSelectDisplay
   */
  const getDisplayValue = useCallback(
    forTooltip => {
      let values = valueInUse;
      if (!Array.isArray(values)) {
        values = [values];
      }

      const text2DsiplayArr = values
        .map(v => {
          let text2Display = options.find(option =>
            isEqual(valueParser(option.value), valueParser(v))
          );
          if (!text2Display) {
            text2Display = '';
          } else {
            text2Display = text2Display.text || '';
          }
          return t(text2Display);
        })
        .filter(v => v);

      if (
        aggregateMultiSelectDisplay &&
        !forTooltip &&
        text2DsiplayArr.length > 1
      ) {
        return text2DsiplayArr[0] + ',...';
      }
      return text2DsiplayArr.join(', ');
    },
    [valueInUse, options, aggregateMultiSelectDisplay, t, valueParser]
  );

  const isSelected = useCallback(
    v => {
      const parsedValue = valueParser(v);
      if (allowMultiSelect) {
        return valueParser(valueInUse).includes(parsedValue);
      }

      return isEqual(valueParser(valueInUse), parsedValue);
    },
    [valueInUse, allowMultiSelect, valueParser]
  );

  const previewTootltipText = useMemo(() => {
    // if tooltip is provided, we use it
    if (tooltip) {
      return tooltip;
    }
    // in a lot of cases we don't even need a tooltip
    const isTooltipNeeded =
      allowMultiSelect && aggregateMultiSelectDisplay && valueInUse.length > 1;

    // but if we do, gotta make sure it's parsable (maybe we used custom parsing and we don't have access to the text)
    // we don't support async options as we can't access text data
    const isGeneratingTooltipPossible = isTooltipNeeded && !asyncOptions;

    if (isTooltipNeeded && isGeneratingTooltipPossible) {
      // isTooltipNeeded is here for readability
      return getDisplayValue(true);
    }
    return '';
  }, [
    allowMultiSelect,
    aggregateMultiSelectDisplay,
    valueInUse,
    asyncOptions,
    getDisplayValue,
    tooltip
  ]);

  const viewProps = {
    t,
    variant: variant ? variant : isMobileView ? 'mobile' : variant,
    forceNonMobileVersion: inputProps.forceNonMobileVersion,
    label,
    searchable,
    useMultiSelect: allowMultiSelect,
    forceDisplayValue,

    placeholder,
    options: options2Display,
    asyncOptions,

    anchorEl,
    setAnchorEl,
    searchInputEl,
    setSearchInputEl,
    selected: valueInUse,
    onSelected,

    disabled,
    filterText,
    setFilterText,
    getDisplayValue,
    menuProps,
    inputProps,
    menuWidth,
    noArrow,
    isSelected,
    openMenuOptionParser,
    isLoading,
    tanslateValue,
    virtualize,
    virtualiziztionProps,
    isUserLookup,
    previewTootltipText,
    tooltipArrow,
    selectRef
  };

  return <SelectView {...viewProps} />;
}
